import * as React from 'react';
import { ReactFlowProvider } from 'reactflow';
import {
	Box,
	Header,
	Grid,
	Hotspot,
	Button,
} from '@amzn/awsui-components-console';
import Tabs from '@amzn/awsui-components-console/tabs';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import TaskTypeList from 'src/components/commonComponents/taskTypeList/taskTypeList';
import ErrorLoader from 'src/components/commonComponents/errorLoader/errorLoader';
import UpdateWFDefinitionCanvas from '../updateWFDefinitionCanvas/updateWFDefinitionCanvas';
import UpdateTaskDefinition from '../updateTaskDefinition/updateTaskDefinition';
import CanvasActionButtons from '../canvasActionButtons/canvasActionButtons';
import WorkflowTaskTable from '../workflowTaskTable/WorkflowTaskTable';
import { useTableTabState } from '../workflowTaskTable/useTableTabState';
import './updateWFDefinitionStep.scss';

export default function UpdateWFDefinitionStep() {
	const fetchTaskTypeList = useStore((state: Store) => state.fetchTaskTypeList);
	const fetchWFDefinition = useStore((state: Store) => state.fetchWFDefinition);
	const WFDefinitionError = useStore((state: Store) => state.WFDefinitionError);
	const isWFDefinitionLoading = useStore(
		(state: Store) => state.isWFDefinitionLoading
	);
	const workflowCreationMetaData = useStore(
		(state: Store) => state.workflowCreationMetaData
	);
	const activeReactFlowNodeId = useStore(
		(state: Store) => state.activeReactFlowNodeId
	);
	const autoLayout = useStore((state: Store) => state.autoLayout);
	const syncCanvasToStore = useStore((state: Store) => state.syncCanvasToStore);
	const setActiveView = useStore((state: Store) => state.setActiveView);
	const resetActiveTaskDefinitionStore = useStore(
		(state: Store) => state.resetActiveTaskDefinitionStore
	);
	const { workflowDefinitionName, workflowId, domains, entityType } =
		workflowCreationMetaData;

	const {
		selectedTableTaskId,
		isTableTaskPanelOpen,
		setIsTableTaskPanelOpen,
		handleTableTaskSelect,
		handleTableTaskEdit,
		resetTableTabState,
	} = useTableTabState();

	const [isTaskPanelCollapsed, setIsTaskPanelCollapsed] = React.useState(false);
	const [isWFDefinitionFetched, setIsWFDefinitionFetched] =
		React.useState(false);
	const [activeTabId, setActiveTabId] = React.useState('canvas');
	const expandPanelRef = React.useRef<() => void>();

	React.useEffect(() => {
		expandPanelRef.current = () => setIsTaskPanelCollapsed(false);
		setActiveView('canvas');
	}, []);

	React.useEffect(() => {
		async function initialRequest() {
			await fetchWFDefinition(workflowId);
			setIsWFDefinitionFetched(true);
		}

		initialRequest();
	}, []);

	React.useEffect(() => {
		if (isWFDefinitionFetched) {
			fetchTaskTypeList(domains, entityType, workflowId);
		}
	}, [isWFDefinitionFetched]);

	React.useEffect(() => {
		if (activeReactFlowNodeId) {
			setIsTaskPanelCollapsed(false);
		}
	}, [activeReactFlowNodeId]);

	const isPageLoading = isWFDefinitionLoading;
	const pageError = WFDefinitionError;

	const handleTabChange = (tabId: string) => {
		const previousTabId = activeTabId;
		setActiveTabId(tabId);
		setActiveView(tabId === 'table' ? 'table' : 'canvas');

		resetActiveTaskDefinitionStore();
		resetTableTabState();

		if (previousTabId === 'canvas' && tabId === 'table') {
			syncCanvasToStore();
		}

		if (previousTabId === 'table' && tabId === 'canvas') {
			setTimeout(() => {
				autoLayout();
			}, 0);
		}
	};

	return (
		<Box className="updateWFDefinitionStep">
			<ErrorLoader isLoading={isPageLoading} error={pageError} />
			{!(isPageLoading || pageError) && (
				<ReactFlowProvider>
					<Tabs
						activeTabId={activeTabId}
						onChange={({ detail }) => handleTabChange(detail.activeTabId)}
						tabs={[
							{
								id: 'canvas',
								label: 'Canvas',
								content: (
									<Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
										<TaskTypeList />
										<Box className="middlePlusRightCntnr">
											<div style={{ marginBottom: '1px' }}>
												<Header variant="h2">
													{workflowDefinitionName}
													<Hotspot side="right" hotspotId="start-tutorial" />
												</Header>
											</div>
											<div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right', width: '100%', flexWrap: 'nowrap', marginBottom: '1px' }}>
												<div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
													<CanvasActionButtons />
													{isTaskPanelCollapsed && (
														<div style={{ padding: '10px 0', margin: '4px 0' }}>
															<Button
																iconName="angle-left"
																onClick={() => setIsTaskPanelCollapsed(false)}
															>
																Show Task Definition
															</Button>
														</div>
													)}
												</div>
											</div>
											<Box className="canvasPlusTaskDFCntnr">
												<Grid
													className="canvasPlusTaskDFCGrid"
													gridDefinition={
														isTaskPanelCollapsed
															? [{ colspan: 12 }]
															: [{ colspan: 8 }, { colspan: 4 }]
													}
												>
													<UpdateWFDefinitionCanvas
														onNodeClick={() => setIsTaskPanelCollapsed(false)}
													/>
													{!isTaskPanelCollapsed && (
														<UpdateTaskDefinition
															onCollapse={() => setIsTaskPanelCollapsed(true)}
														/>
													)}
												</Grid>
											</Box>
										</Box>
									</Grid>
								),
							},
							{
								id: 'table',
								label: 'Table',
								content: (
									<Box className="middlePlusRightCntnr">
										<Box className="canvasPlusTaskDFCntnr">
											<Grid
												className="canvasPlusTaskDFCGrid"
												gridDefinition={
													isTableTaskPanelOpen
														? [{ colspan: 8 }, { colspan: 4 }]
														: [{ colspan: 12 }]
												}
											>
												<div style={{ overflow: 'auto', height: '100%' }}>
													<WorkflowTaskTable
														onTaskSelect={handleTableTaskSelect}
														onTaskEdit={handleTableTaskEdit}
														selectedTaskId={selectedTableTaskId}
													/>
												</div>
												{isTableTaskPanelOpen && (
													<UpdateTaskDefinition
														onCollapse={() => setIsTableTaskPanelOpen(false)}
														hideDuplicate
													/>
												)}
											</Grid>
										</Box>
									</Box>
								),
							},
						]}
					/>
				</ReactFlowProvider>
			)}
		</Box>
	);
}
